import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import { dyn, scheduleForCleanup } from "trader.ts/ui";
import { Field, Parsing } from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import { isEqual } from "../lib/isequal";
import * as FA from "./../../styles/fontawesome.css.classes";
import * as style from "./../../styles/style.css.classes";

export const arrayField = makeArrayField;
export async function makeArrayField<T>(opts: {
  initialVal: T[];
  makeSubForm: (t: T, i: number) => Promise<Field<T>>;
  makeNew: () => T;
  render?: (
    arr: { render: () => HTMLElement | HTMLElement[]; s: Source<Parsing<T>> }[],
    addNew: () => void,
    remove: (i: number) => void,
    moveUp: (i: number) => void,
    moveDown: (i: number) => void,
  ) => HTMLElement | HTMLElement[];
}): Promise<Field<T[]>> {
  const parsingS: Source<Parsing<T[]>> = new Source({
    tag: "parsed",
    parsed: opts.initialVal,
  });

  const cleanupFuncs: (() => void)[] = [];
  function addToCleanupInternal(f: () => void) {
    cleanupFuncs.push(f);
  }
  function doFullCleanup() {
    cleanupFuncs.forEach((f) => f());
  }

  // Forward declaration
  const arrS: Source<Field<T>[]> = new Source([]);

  function doParse() {
    const arr = arrS.get().map((subf) => subf.s.get());
    parsingS.set(parseInternal(arr));
  }

  await reInit(opts.initialVal);

  function parseInternal(arr: Parsing<T>[]): Parsing<T[]> {
    const res: T[] = [];
    for (const el of arr) {
      if (el.tag === "parsed") {
        res.push(el.parsed);
      } else if (el.tag === "err") {
        return { tag: "err", label: el.label };
      } else if (el.tag === "loading") {
        return { tag: "loading" };
      } else if (el.tag === "initial") {
        return { tag: "initial" };
      } else {
        return checkAllCasesHandled(el);
      }
    }
    return { tag: "parsed", parsed: res };
  }

  async function reInit(ts: T[]) {
    const newArr = await Promise.all(ts.map((t, j) => opts.makeSubForm(t, j)));
    arrS.set(newArr);
    doParse();
    doFullCleanup();
    arrS.get().map((ff) => addToCleanupInternal(ff.s.observe(doParse)));
  }

  function addNew() {
    void opts.makeSubForm(opts.makeNew(), arrS.get().length).then((newF) => {
      arrS.set(arrS.get().concat(newF));
      doParse();
      doFullCleanup();
      arrS.get().map((ff) => addToCleanupInternal(ff.s.observe(doParse)));
    });
  }
  function remove(i: number) {
    const curr = arrS.get();
    arrS.set(curr.slice(0, i).concat(curr.slice(i + 1)));
    doParse();
    doFullCleanup();
    arrS.get().map((ff) => addToCleanupInternal(ff.s.observe(doParse)));
  }
  function moveUp(i: number) {
    const curr = arrS.get();
    if (i === 0) {
      return;
    }
    if (curr.length === 1) {
      return;
    }
    arrS.set([
      ...curr.slice(0, i - 1),
      curr[i],
      curr[i - 1],
      ...curr.slice(i + 1),
    ]);
    doParse();
    doFullCleanup();
    arrS.get().map((ff) => addToCleanupInternal(ff.s.observe(doParse)));
  }

  function moveDown(i: number) {
    const curr = arrS.get();
    if (i >= curr.length - 1) {
      return;
    }
    if (curr.length === 1) {
      return;
    }
    arrS.set([...curr.slice(0, i), curr[i + 1], curr[i], ...curr.slice(i + 2)]);
    doParse();
    doFullCleanup();
    arrS.get().map((ff) => addToCleanupInternal(ff.s.observe(doParse)));
  }

  function render() {
    const curr = parsingS.get();
    if (curr.tag === "parsed") {
      void reInit(curr.parsed);
    }

    scheduleForCleanup(doFullCleanup);

    scheduleForCleanup(
      parsingS.observe((parsing) => {
        if (parsing.tag === "parsed") {
          const arr = arrS.get().map((subf) => subf.s.get());
          const currentRaw = parseInternal(arr);
          if (currentRaw.tag === "parsed") {
            if (isEqual(parsing.parsed, currentRaw.parsed)) {
              // Both sides are parsed and are equal -> do nothing
            } else {
              // Both sides are parsed and are equal -> do nothing
              void reInit(parsing.parsed);
            }
          } else {
            // Underlying fields are in error, or loading, or something else -> re-init
            void reInit(parsing.parsed);
          }
        }
      }),
    );
    return (
      <div>
        {dyn(arrS, (arr) =>
          (opts.render || defaultArrayRender)(
            arr,
            addNew,
            remove,
            moveUp,
            moveDown,
          ),
        )}
      </div>
    );
  }

  return {
    s: parsingS,
    render,
    cleanup: () => {},
  };
}

function defaultArrayRender<T>(
  arr: Field<T>[],
  addNew: () => void,
  remove: (i: number) => void,
  moveUp: (i: number) => void,
  moveDown: (i: number) => void,
): HTMLElement {
  return (
    <div>
      {arr.map((subf, i) => (
        <section style=" padding: 16px; background-color: var(--grey-000); border-radius: 16px; border: 1px solid var(--grey-200);">
          {subf.render()}
          <div style="text-align: right;">
            <button
              className={style.button_has_only_icon + style.button_em_down}
              onclick={() => moveUp(i)}
            >
              <i
                className={FA.icon_arrow_right}
                style="transform: rotate(270deg)"
              ></i>
            </button>
            <button
              className={style.button_has_only_icon + style.button_em_down}
              onclick={() => moveDown(i)}
            >
              <i
                className={FA.icon_arrow_right}
                style="transform: rotate(90deg)"
              ></i>
            </button>
            <button
              className={style.button_has_only_icon + style.button_em_down}
              onclick={() => remove(i)}
            >
              <i className={FA.icon_trash_alt_solid}></i>
            </button>
          </div>
        </section>
      ))}
      <div style="text-align: right">
        <button onclick={addNew}> {"+ " + "Voeg toe"} </button>
      </div>
    </div>
  );
}

export function oneLineArrayRender<T>(
  rowIsEmpty: (t: T, i: number, opts: { arrayLength: number }) => boolean,
  opts?: {
    minimumOne?: boolean;
    allowNewAndRemove?: boolean;
    headers?: string[];
  },
): (
  arr: { render: () => HTMLElement | HTMLElement[]; s: Source<Parsing<T>> }[],
  addNew: () => void,
  remove: (i: number) => void,
) => HTMLElement | HTMLElement[] {
  const smallButtonStyle = `
    min-width: 40px;
    max-width: 40px;
    padding-left: 0px;
    padding-right: 0px;
    height: var(--input-height);
    grid-column: -1;
`;
  const renderInvisibleButton = () => (
    <div
      className={style.input_like}
      style={smallButtonStyle + "pointer-events: none;"}
    ></div>
  );

  const allowNewAndRemove = opts?.allowNewAndRemove ?? true;
  return function (arr, addNew, remove) {
    function renderAddButton() {
      if (allowNewAndRemove === false) {
        return [];
      }
      return (
        <button style={smallButtonStyle} onclick={addNew}>
          <i className={FA.icon_plus_solid} style="margin: 0px;"></i>
        </button>
      );
    }
    function renderRemoveButton(i: number) {
      if (allowNewAndRemove === false) {
        return [];
      }
      return (
        <button
          className={style.button_em_down}
          style={smallButtonStyle}
          onclick={() => remove(i)}
        >
          <i className={FA.icon_trash_alt_solid} style="margin: 0px;"></i>
        </button>
      );
    }
    const rows = arr.map((row, i) => {
      const button = dyn(row.s, (vals) => {
        // last button
        if (i === arr.length - 1) {
          if (
            vals.tag === "parsed" &&
            !rowIsEmpty(vals.parsed, i, { arrayLength: arr.length })
          ) {
            return renderAddButton();
          } else if (i === 0 && opts?.minimumOne === true) {
            return renderInvisibleButton();
          } else {
            return renderRemoveButton(i);
          }
        }
        return renderRemoveButton(i);
      });

      const rowRendered = row.render();

      return (
        <div style="flex: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(1px, 1fr)); grid-gap: 0px 12px;">
          {rowRendered}
          {button}
        </div>
      );
    });
    const renderedHeaders = opts?.headers
      ? opts.headers.map((header) => (
          <div>
            <label>{header}</label>
          </div>
        ))
      : [];
    return (
      <div>
        <div style="flex: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(1px, 1fr)); grid-gap: 0px 12px;">
          {rows.length === 0
            ? [...renderedHeaders, renderAddButton()]
            : [
                ...renderedHeaders,
                <div style={smallButtonStyle + "height: unset;"}></div>,
              ]}
        </div>
        {rows}
      </div>
    );
  };
}

export function oneLineArrayRenderWithAlwaysAddAndDelete<T>(opts?: {
  headers?: string[];
}): (
  arr: { render: () => HTMLElement | HTMLElement[]; s: Source<Parsing<T>> }[],
  addNew: () => void,
  remove: (i: number) => void,
) => HTMLElement | HTMLElement[] {
  const smallButtonStyle = (grid_column: number) => `
    min-width: 40px;
    max-width: 40px;
    padding-left: 0px;
    padding-right: 0px;
    height: var(--input-height);
    margin-left: 0px;
  grid-column: ${grid_column};
  `;
  const renderInvisibleButton = (grid_column: number) => (
    <div
      className={style.input_like}
      style={
        smallButtonStyle(grid_column) +
        `pointer-events: none; visibility: hidden; height: 2px; min-height: unset;`
      }
    ></div>
  );

  return function (arr, addNew, remove) {
    const addButton = (grid_column: number) => (
      <button style={smallButtonStyle(grid_column)} onclick={addNew}>
        <i className={FA.icon_plus_solid} style="margin: 0px;"></i>
      </button>
    );
    function renderRemoveButton(i: number, grid_column: number) {
      return (
        <button
          className={style.button_em_down}
          style={smallButtonStyle(grid_column)}
          onclick={() => remove(i)}
        >
          <i className={FA.icon_trash_alt_solid} style="margin: 0px;"></i>
        </button>
      );
    }
    const rows = arr.map((row, i) => {
      const rowRendered = row.render();

      return (
        <div style="flex: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)) 40px; grid-gap: 0px 12px;">
          {rowRendered}
          {renderRemoveButton(i, -2)}
          {addButton(-1)}
        </div>
      );
    });
    const renderedHeaders = opts?.headers
      ? opts.headers.map((header) => (
          <div>
            <label>{header}</label>
          </div>
        ))
      : [];
    return rows.length === 0 ? (
      <article>
        <div>
          <div style="flex: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)) 40px; grid-gap: 0px 12px;">
            {renderedHeaders}
          </div>
        </div>
        <button
          className={style.button_has_only_icon + style.button_em_down}
          onclick={addNew}
          style={"width: 100%"}
        >
          <i className={FA.icon_plus_solid}></i>
        </button>
      </article>
    ) : (
      <article>
        <div style="flex: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)) 40px; grid-gap: 0px 12px;">
          {renderedHeaders}
          {renderInvisibleButton(-2)}
          {renderInvisibleButton(-1)}
        </div>
        {rows}
      </article>
    );
  };
}
